import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent } from '@/components/ui/tabs'
import { TabGroup } from '@/components/ui/tab-group'
import { NodeAvatarWithStatus, NodeList, NodeListItem } from '@/components/nodes'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { UserHoverCard } from '@/components/users/UserHoverCard'
import { PageHeader } from '@/components/layout/PageHeader'
import { useAuth } from '@/hooks/useAuth'
import { useOnboardingRedirect } from '@/hooks/useOnboardingRedirect'
import {
  projectService,
  taskService,
  messageService,
  workspaceService,
  userService,
} from '@/services'
import {
  Status,
  MessageStatus,
  UserStatus,
  type Task,
  type Project,
  type Message,
  type User,
} from '@/types'
import { List, Plus } from 'lucide-react'
import {
  homeProjectTabs,
  homeTaskTabs,
  homeMessageTabs,
  homeCollaboratorTabs,
} from '@/lib/config/tabs'

type TaskTabValue = 'assigned' | 'drafts'
type ProjectTabValue = 'my-projects' | 'my-drafts'
type MessageTabValue = 'unread' | 'drafts'
type CollaboratorTabValue = 'online' | 'ready-to-chat' | 'deep-work'

export default function HomePage() {
  const { user } = useAuth()
  const [projects, setProjects] = useState<Project[]>([])
  const [tasks, setTasks] = useState<Task[]>([])
  const [messages, setMessages] = useState<Message[]>([])
  const [collaborators, setCollaborators] = useState<User[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [activeTaskTab, setActiveTaskTab] = useState<TaskTabValue>('assigned')
  const [activeProjectTab, setActiveProjectTab] = useState<ProjectTabValue>('my-projects')
  const [activeMessageTab, setActiveMessageTab] = useState<MessageTabValue>('unread')
  const [activeCollaboratorTab, setActiveCollaboratorTab] = useState<CollaboratorTabValue>('online')

  // Check if user needs onboarding
  useOnboardingRedirect()

  // Get time-based greeting
  const getGreeting = (): string => {
    const hour = new Date().getHours()
    if (hour < 12) return 'morning'
    if (hour < 18) return 'afternoon'
    return 'evening'
  }

  // Get user's first name
  const getFirstName = (): string => {
    if (!user) return ''
    return user.name.split(' ')[0]
  }

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return

      try {
        setIsLoading(true)

        // Get all projects for current user
        const allProjects = await projectService.getAll()
        const userProjects = allProjects.filter(
          (project) => project.creator.id === user.id || project.users.some((u) => u.id === user.id)
        )
        setProjects(userProjects)

        // Get all tasks for current user
        const allTasks = await taskService.getAll()
        const userTasks = allTasks.filter(
          (task) =>
            task.assignee?.id === user.id ||
            task.creator.id === user.id ||
            task.subtasks?.some((subtask) => subtask.assignee?.id === user.id)
        )
        setTasks(userTasks)

        // Get all messages for current user
        const userMessages = await messageService.getMessagesForUser(user.id)
        setMessages(userMessages)

        // Get all online collaborators across all workspaces
        const userWorkspaces = await workspaceService.getWorkspacesForUser(user.id)
        const allUsers = await userService.getAll()

        // Get unique users from all workspaces (excluding current user)
        const workspaceUserIds = new Set<string>()
        userWorkspaces.forEach((workspace) => {
          workspace.users.forEach((workspaceUser) => {
            if (workspaceUser.id !== user.id) {
              workspaceUserIds.add(workspaceUser.id)
            }
          })
        })

        // Filter for online users
        const onlineCollaborators = allUsers.filter((u) => {
          const isOnline =
            u.status === UserStatus.Online ||
            u.status === UserStatus.OnlineReadyToChat ||
            u.status === UserStatus.OnlineInDeepWork
          return workspaceUserIds.has(u.id) && isOnline
        })

        setCollaborators(onlineCollaborators)
      } catch (error) {
        console.error('Error fetching home data:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [user])

  // Helper function to check if project is associated with user
  const isProjectAssociatedWithUser = (project: Project): boolean => {
    if (!user) return false
    return project.creator.id === user.id || project.users.some((u) => u.id === user.id)
  }

  // Filter projects based on active tab
  const getFilteredProjects = (): Project[] => {
    switch (activeProjectTab) {
      case 'my-projects':
        // Projects associated with current user and active (To Do or In Progress)
        return projects.filter(
          (project) =>
            isProjectAssociatedWithUser(project) &&
            [Status.ToDo, Status.InProgress].includes(project.status)
        )
      case 'my-drafts':
        // Draft projects created by current user
        return projects.filter(
          (project) => project.creator.id === user?.id && project.status === Status.Draft
        )
      default:
        return []
    }
  }

  const filteredProjects = getFilteredProjects().slice(0, 5) // Show only top 5 projects

  // Helper function to check if task is associated with user
  const isTaskAssociatedWithUser = (task: Task): boolean => {
    if (!user) return false
    return (
      task.assignee?.id === user.id ||
      task.creator.id === user.id ||
      task.subtasks?.some((subtask) => subtask.assignee?.id === user.id)
    )
  }

  // Filter tasks based on active tab
  const getFilteredTasks = (): Task[] => {
    let filteredTasks: Task[] = []

    switch (activeTaskTab) {
      case 'assigned':
        // Active tasks assigned to current user (To Do or In Progress)
        filteredTasks = tasks.filter(
          (task) =>
            task.assignee?.id === user?.id && [Status.ToDo, Status.InProgress].includes(task.status)
        )
        break
      case 'drafts':
        // Draft tasks associated with current user
        filteredTasks = tasks.filter(
          (task) => isTaskAssociatedWithUser(task) && task.status === Status.Draft
        )
        break
      default:
        return []
    }

    // Sort tasks by due date (nearest first)
    // Tasks without due dates go to the end
    return filteredTasks.sort((a, b) => {
      // If both tasks have no due date, maintain original order
      if (!a.dueDate && !b.dueDate) {
        return 0
      }

      // If task A has no due date but task B does, task B comes first
      if (!a.dueDate && b.dueDate) {
        return 1
      }

      // If task A has due date but task B doesn't, task A comes first
      if (a.dueDate && !b.dueDate) {
        return -1
      }

      // Both tasks have due dates, compare them chronologically
      return new Date(a.dueDate!).getTime() - new Date(b.dueDate!).getTime()
    })
  }

  const filteredTasks = getFilteredTasks().slice(0, 5) // Show only top 5 tasks

  // Filter messages based on active tab
  const getFilteredMessages = (): Message[] => {
    if (!user) return []

    switch (activeMessageTab) {
      case 'unread':
        // Unread messages for current user
        return messages.filter(
          (message) =>
            message.status === MessageStatus.Published &&
            !message.readBy.some((read) => read.userId === user.id)
        )
      case 'drafts':
        // Draft messages created by current user
        return messages.filter(
          (message) => message.status === MessageStatus.Draft && message.creator.id === user.id
        )
      default:
        return []
    }
  }

  const filteredMessages = getFilteredMessages().slice(0, 3) // Show only top 5 messages

  // Filter collaborators based on active tab
  const getFilteredCollaborators = (): User[] => {
    switch (activeCollaboratorTab) {
      case 'online':
        // All online users (any online status)
        return collaborators
      case 'ready-to-chat':
        // Only users ready to chat
        return collaborators.filter((user) => user.status === UserStatus.OnlineReadyToChat)
      case 'deep-work':
        // Only users in deep work
        return collaborators.filter((user) => user.status === UserStatus.OnlineInDeepWork)
      default:
        return collaborators
    }
  }

  const filteredCollaborators = getFilteredCollaborators()

  return (
    <TooltipProvider>
      <div className="space-y-6">
        <PageHeader title="Home" description={`Good ${getGreeting()}, ${getFirstName()}`} />

        <div className="grid gap-4 md:grid-cols-2 min-w-0">
          <Card className="min-w-0">
            <CardHeader>
              <div className="flex flex-row items-center justify-between gap-2">
                <div className="space-y-1">
                  <CardTitle>My Projects</CardTitle>
                </div>
                <div className="flex items-center gap-2">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link
                        to="/projects"
                        className="text-muted-foreground hover:text-primary transition-colors"
                      >
                        <List className="h-5 w-5" />
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>View all projects</p>
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link
                        to="/projects/new"
                        className="text-muted-foreground hover:text-primary transition-colors"
                      >
                        <Plus className="h-5 w-5" />
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Create new project</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-16 bg-muted rounded animate-pulse" />
                  ))}
                </div>
              ) : (
                <Tabs
                  value={activeProjectTab}
                  onValueChange={(value) => setActiveProjectTab(value as ProjectTabValue)}
                >
                  <TabGroup
                    tabs={homeProjectTabs}
                    className="mb-4"
                    value={activeProjectTab}
                    onValueChange={(value) => setActiveProjectTab(value as ProjectTabValue)}
                  />

                  <TabsContent value="my-projects" className="mt-0">
                    <NodeList
                      items={filteredProjects}
                      context="project"
                      config={{
                        showStatus: false,
                        showWorkspace: true,
                        showUserCount: false,
                        showPriority: true,
                        showHorizon: true,
                        hoverMode: true,
                        showCreator: false,
                        emptyMessage: 'No active projects',
                        emptyState: 'No active projects',
                      }}
                    />
                  </TabsContent>

                  <TabsContent value="my-drafts" className="mt-0">
                    <NodeList
                      items={filteredProjects}
                      context="project"
                      config={{
                        showStatus: false,
                        showWorkspace: true,
                        showUserCount: false,
                        showPriority: false,
                        showHorizon: false,
                        showType: false,
                        showCreator: false,
                        emptyMessage: 'No draft projects',
                        emptyState: 'No draft projects',
                      }}
                    />
                  </TabsContent>
                </Tabs>
              )}
            </CardContent>
          </Card>

          <Card className="min-w-0">
            <CardHeader>
              <div className="flex flex-row items-center justify-between gap-2">
                <div className="space-y-1">
                  <CardTitle>My Tasks</CardTitle>
                </div>
                <div className="flex items-center gap-2">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link
                        to="/tasks"
                        className="text-muted-foreground hover:text-primary transition-colors"
                      >
                        <List className="h-5 w-5" />
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>View all tasks</p>
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link
                        to="/tasks/new"
                        className="text-muted-foreground hover:text-primary transition-colors"
                      >
                        <Plus className="h-5 w-5" />
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Create new task</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-16 bg-muted rounded animate-pulse" />
                  ))}
                </div>
              ) : (
                <Tabs
                  value={activeTaskTab}
                  onValueChange={(value) => setActiveTaskTab(value as TaskTabValue)}
                >
                  <TabGroup
                    tabs={homeTaskTabs}
                    className="mb-4"
                    value={activeTaskTab}
                    onValueChange={(value) => setActiveTaskTab(value as TaskTabValue)}
                  />

                  <TabsContent value="assigned" className="mt-0">
                    <NodeList
                      items={filteredTasks}
                      context="task"
                      config={{
                        showStatus: false,
                        showPriority: true,
                        showHorizon: true,
                        showDescription: false,
                        showWorkspace: true,
                        showAssignee: false,
                        showDueDate: true,
                        showSubtasks: false,
                        hoverMode: true,
                        emptyMessage: 'No assigned tasks',
                      }}
                    />
                  </TabsContent>

                  <TabsContent value="associated" className="mt-0">
                    <NodeList
                      items={filteredTasks}
                      context="task"
                      config={{
                        showStatus: false,
                        showPriority: false,
                        showHorizon: false,
                        showDescription: false,
                        showWorkspace: true,
                        showAssignee: false,
                        showDueDate: false,
                        showSubtasks: false,
                        emptyMessage: 'No associated tasks',
                      }}
                    />
                  </TabsContent>

                  <TabsContent value="drafts" className="mt-0">
                    <NodeList
                      items={filteredTasks}
                      context="task"
                      config={{
                        showStatus: false,
                        showPriority: false,
                        showDescription: false,
                        showWorkspace: true,
                        showAssignee: false,
                        showDueDate: false,
                        showSubtasks: false,
                        showHorizon: false,
                        showType: false,
                        emptyMessage: 'No draft tasks',
                      }}
                    />
                  </TabsContent>
                </Tabs>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-4 md:grid-cols-2 min-w-0">
          <Card className="min-w-0">
            <CardHeader>
              <div className="flex flex-row items-center justify-between gap-2">
                <div className="space-y-1">
                  <CardTitle>My Messages</CardTitle>
                </div>
                <div className="flex items-center gap-2">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link
                        to="/messages"
                        className="text-muted-foreground hover:text-primary transition-colors"
                      >
                        <List className="h-5 w-5" />
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>View all messages</p>
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link
                        to="/messages/new"
                        className="text-muted-foreground hover:text-primary transition-colors"
                      >
                        <Plus className="h-5 w-5" />
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Create new message</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-16 bg-muted rounded animate-pulse" />
                  ))}
                </div>
              ) : (
                <Tabs
                  value={activeMessageTab}
                  onValueChange={(value) => setActiveMessageTab(value as MessageTabValue)}
                >
                  <TabGroup
                    tabs={homeMessageTabs}
                    className="mb-4"
                    value={activeMessageTab}
                    onValueChange={(value) => setActiveMessageTab(value as MessageTabValue)}
                  />

                  <TabsContent value="unread" className="mt-0">
                    {filteredMessages.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-8">
                        No unread messages
                      </p>
                    ) : (
                      <div className="flex flex-col space-y-3">
                        {filteredMessages.map((message) => (
                          <NodeListItem
                            key={message.id}
                            item={message}
                            context="message"
                            config={{
                              showCreator: true,
                              showDate: true,
                              showReplyCount: true,
                              showDescription: false,
                              showRelationshipCounts: false,
                              showReadStatus: true,
                              showWorkspace: false,
                            }}
                          />
                        ))}
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="drafts" className="mt-0">
                    {filteredMessages.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-8">
                        No draft messages
                      </p>
                    ) : (
                      <div className="flex flex-col space-y-3">
                        {filteredMessages.map((message) => (
                          <NodeListItem
                            key={message.id}
                            item={message}
                            context="message"
                            config={{
                              showCreator: true,
                              showDate: false,
                              showReplyCount: false,
                              showDescription: true,
                              showRelationshipCounts: false,
                            }}
                          />
                        ))}
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              )}
            </CardContent>
          </Card>

          <Card className="min-w-0">
            <CardHeader>
              <div className="flex flex-row items-center justify-between gap-2">
                <div className="space-y-1">
                  <CardTitle>My Collaborators</CardTitle>
                </div>
                <div className="flex items-center gap-2">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link
                        to="/users"
                        className="text-muted-foreground hover:text-primary transition-colors"
                      >
                        <List className="h-5 w-5" />
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>View all users</p>
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link
                        to="/users/new"
                        className="text-muted-foreground hover:text-primary transition-colors"
                      >
                        <Plus className="h-5 w-5" />
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Invite new user</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-4">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div key={i} className="flex flex-col items-center gap-2">
                        <div className="h-12 w-12 bg-muted rounded-full animate-pulse" />
                        <div className="h-4 w-16 bg-muted rounded animate-pulse" />
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <Tabs
                  value={activeCollaboratorTab}
                  onValueChange={(value) => setActiveCollaboratorTab(value as CollaboratorTabValue)}
                >
                  <TabGroup
                    tabs={homeCollaboratorTabs}
                    className="mb-4"
                    value={activeCollaboratorTab}
                    onValueChange={(value) =>
                      setActiveCollaboratorTab(value as CollaboratorTabValue)
                    }
                  />

                  <TabsContent value="online" className="mt-6">
                    {filteredCollaborators.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-8">
                        No collaborators online
                      </p>
                    ) : (
                      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-4">
                        {filteredCollaborators.map((collaborator) => (
                          <UserHoverCard key={collaborator.id} user={collaborator}>
                            <NodeAvatarWithStatus
                              user={collaborator}
                              size="lg"
                              className="border-2 border-background ring-2 ring-muted group-hover:ring-primary transition-all"
                              linkToProfile={true}
                              showHoverCard={true}
                            />
                          </UserHoverCard>
                        ))}
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="ready-to-chat" className="mt-0">
                    {filteredCollaborators.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-8">
                        No collaborators ready to chat
                      </p>
                    ) : (
                      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-4">
                        {filteredCollaborators.map((collaborator) => (
                          <UserHoverCard key={collaborator.id} user={collaborator}>
                            <NodeAvatarWithStatus
                              user={collaborator}
                              size="lg"
                              className="border-2 border-background ring-2 ring-muted group-hover:ring-primary transition-all"
                              linkToProfile={true}
                              showHoverCard={true}
                            />
                          </UserHoverCard>
                        ))}
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="deep-work" className="mt-0">
                    {filteredCollaborators.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-8">
                        No collaborators in deep work
                      </p>
                    ) : (
                      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-4">
                        {filteredCollaborators.map((collaborator) => (
                          <UserHoverCard key={collaborator.id} user={collaborator}>
                            <NodeAvatarWithStatus
                              user={collaborator}
                              size="lg"
                              className="border-2 border-background ring-2 ring-muted group-hover:ring-primary transition-all"
                              linkToProfile={true}
                              showHoverCard={true}
                            />
                          </UserHoverCard>
                        ))}
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </TooltipProvider>
  )
}
